import { useCallback, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useTheme, Box } from "@mui/material"
import _ from "lodash"

import { useMap } from "hooks/map/useMap"
import { useOrdinalMap } from "hooks/ordinal"
import {
  useFlyToMap,
  useGetUserLocationView,
  useGetMapViewFromUrl,
} from "hooks/map"

import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"

import { pluralize } from "utils/string"

import { OrdinalSelector } from "components/OrdinalSelector"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { VenueSelector } from "components/VenueSelector"
import { OrdinalSelectMenu } from "components/OrdinalSelector/OrdinalSelectMenu"
import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { OrdinalSelectSplide } from "components/OrdinalSelector/OrdinalSelectSplide"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"
import { MyLocationButton } from "components/Button"
import { convertMapViewObjectToPath } from "utils/map"

export const MapOrdinalKioskVPage = ({
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { userLocation } = useGeoLocation()
  const navigate = useNavigate()
  const { ordinal } = useParams()
  const { search: searchQueryString } = useLocation()
  const { openSearch } = useAppUI()
  const { t } = useTranslation()
  const { venues, isMultiOrdinalVenue, isMultiVenueProject } = useVenue()
  const {
    viewingVenueId,
    viewingVenueView,
    viewingVenueLevels,
    quickSearchCategories,
    onChangeVenue,
    locationVenueId,
    locationOrdinal,
  } = useMap()

  const parsedOrdinal = Number(ordinal)

  useOrdinalMap(parsedOrdinal)
  const userLocationView = useGetUserLocationView()
  const hasUserLocationAtCurrentFloor = locationOrdinal === parsedOrdinal
  const { mapView } = useGetMapViewFromUrl()
  useFlyToMap(mapView || userLocationView || viewingVenueView)

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppMap`] || {}

  const {
    actionButton,
    actionContainer,
    toolbarContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    venueSelector,
    navigationWrapper,
    locationButtonContainer,
    ordinalSelectMenu,
    ordinalSelector,
    searchBarContainer,
    ordinalSelectSplide,
    languageSelectMenu,
  } = styleOverrides

  const onClickOrdinal = useCallback(
    (ordinal) => {
      navigate(
        `/maps/${
          ordinal !== null ? `ordinal/${ordinal}${searchQueryString}` : ""
        }`,
        { replace: true }
      )
    },
    [navigate, searchQueryString]
  )

  const handleClickQuickSearch = useCallback(
    (type, category) => {
      //This condition prevent navigation when ordinal or viewingVenueId is undefined
      if (_.isNil(ordinal) || _.isNil(viewingVenueId)) return
      const pluralType = pluralize(type)
      navigate(
        `/maps/${pluralType}/${category}?from-ordinal=${ordinal}&venue=${viewingVenueId}`
      )
    },
    [navigate, ordinal, viewingVenueId]
  )

  const handleClickMyLocation = useCallback(() => {
    //This condition prevent navigation when ordinal is undefined
    if (_.isNil(ordinal) || !userLocation) return
    const userOrdinal = _.get(userLocation, "properties.ordinal", 0)
    const mapViewPath = convertMapViewObjectToPath(userLocationView)
    navigate(`/maps/ordinal/${userOrdinal}?mv=${mapViewPath}`, {
      replace: true,
    })
  }, [navigate, ordinal, userLocationView, userLocation])

  useEffect(() => {
    if (hasUserLocationAtCurrentFloor) handleClickMyLocation()
  }, [hasUserLocationAtCurrentFloor, handleClickMyLocation])

  return (
    <>
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <OrdinalSelector
            selectedOrdinal={parsedOrdinal}
            levels={viewingVenueLevels}
            onClickOrdinal={onClickOrdinal}
            locationOrdinal={locationOrdinal}
          />
        </Box>
      )}
      {isMultiVenueProject && (
        <Box sx={venueSelector}>
          <VenueSelector
            selectedVenue={viewingVenueId}
            venues={venues}
            onClickVenue={onChangeVenue}
            currentVenue={locationVenueId}
          />
        </Box>
      )}
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelectMenu
            selectedOrdinal={parsedOrdinal}
            levels={viewingVenueLevels}
            onChangeOrdinal={onClickOrdinal}
          />
        </Box>
      )}
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectSplide}>
          <OrdinalSelectSplide
            selectedOrdinal={parsedOrdinal}
            levels={viewingVenueLevels}
            onChangeOrdinal={onClickOrdinal}
          />
        </Box>
      )}

      {userLocation && (
        <Box sx={locationButtonContainer}>
          <MyLocationButton onClick={handleClickMyLocation} />
        </Box>
      )}

      <Box key="app-navigation" sx={navigationWrapper}>
        <Box sx={quickLinkContainer}>
          <FeatureChipToggleBar
            categories={quickSearchCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
        <Box sx={toolbarContainer}>
          <Box sx={actionContainer}>
            <LanguageButton variant="contained" sx={actionButton} />
            <LanguageSelectMenu sx={languageSelectMenu} />
            {/* //TODO:: Add a wheel chair's mode handler function later */}
            {/*  <Button
            variant="contained"
            sx={actionButton}
            onClick={() => {}}
            startIcon={<RestroomWheelchairIcon />}
          /> */}
            {additionalMenu}
            {/* //TODO:: Create Toolbar component and refactor Code */}
          </Box>
          <Box sx={searchBarContainer}>
            <SearchToggleButton
              placeholder={t("Search shops and more...")}
              onClick={openSearch}
            />
          </Box>
        </Box>

        {AppNavigation && <AppNavigation />}
      </Box>
    </>
  )
}
